import React from 'react';
import {useTranslations} from 'next-intl';
import {Grid, Row, Col} from '@/grid';
import Container from '@/container';
import {withMarginBottomLg, withMarginBottomXxl} from '@/hocs/withSpacing';
import {SectionPadding} from '@/section';
import {H2Neutral50, H3Neutral50, H4} from '@/typography';
import {withBgGreen900, withNeutral50} from '@/hocs/withColors';
import {GridLayout, GridItem} from '@/ui/grid/grid-layout';
import BoxGrid from '@/ui/box-grid';
import boxGridStyles from '@/ui/box-grid/box-grid.module.scss';
import {ListUnstyled} from '@/ui/lists/list-icons';
import {
  withDotCavoWaters,
  withDotYellow400,
  withDotNeonVioletFlowers,
  withDotNeonAegeanSea
} from '@/hocs/withFontType';
import styles from './styles.module.scss';

const Green900Section = withBgGreen900(SectionPadding);
const H2Neutral50Margined = withMarginBottomXxl(H2Neutral50);
const H3Neutral50Margined = withMarginBottomLg(H3Neutral50);
const DivNeutral50 = withNeutral50('div');
const H4DotCavoWaters = withMarginBottomLg(withDotCavoWaters(H4));
const H4DotYellow400 = withMarginBottomLg(withDotYellow400(H4));
const H4DotNeonVioletFlowers = withMarginBottomLg(withDotNeonVioletFlowers(H4));
const H4DotNeonAegeanSea = withMarginBottomLg(withDotNeonAegeanSea(H4));
const RowMargined = withMarginBottomXxl(Row);
const fullWidth = boxGridStyles['full-width'];

const PlansFeatures = () => {
  const t = useTranslations('pricing.plansSection.plansFeatures');

  const CandidateSourcing = t.raw('candidateSourcing');
  const ApplicantTracking = t.raw('applicantTracking');
  const HRInformation = t.raw('hrInformation');
  const TimeAttendance = t.raw('timeAttendance');

  const ApplicantTracking_splitter = Math.floor(ApplicantTracking.length / 2);
  const HRInformation_splitter = Math.ceil(HRInformation.length / 2);

  const firstColApplicant = ApplicantTracking.slice(0, ApplicantTracking_splitter);
  const secondColApplicant = ApplicantTracking.slice(ApplicantTracking_splitter);
  const firstColHRInformation = HRInformation.slice(0, HRInformation_splitter);
  const secondColHRInformation = HRInformation.slice(HRInformation_splitter);

  return (
    <Green900Section>
      <Container>
        <Grid fluid>
          <Row center="xs">
            <Col xs={12}>
              <H2Neutral50Margined>{t('title')}</H2Neutral50Margined>
              <H3Neutral50Margined>{t('coreRecruitingFeatures')}</H3Neutral50Margined>
            </Col>
          </Row>
          <RowMargined>
            <Col xs={12}>
              <GridLayout Col={12} autoRows>
                <GridItem Col={5}>
                  <BoxGrid
                    className={fullWidth}
                    backgroundColor="transparent"
                    border="green300">
                    <DivNeutral50>
                      <H4DotCavoWaters>{t('candidateSourcingSuite')}</H4DotCavoWaters>
                      <ListUnstyled>
                        {CandidateSourcing.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ListUnstyled>
                    </DivNeutral50>
                  </BoxGrid>
                </GridItem>
                <GridItem Col={7}>
                  <BoxGrid
                    className={fullWidth}
                    backgroundColor="transparent"
                    border="green300">
                    <DivNeutral50>
                      <H4DotYellow400>{t('applicantTrackingSystem')}</H4DotYellow400>
                      <Row className={styles['feature-lists']}>
                        <Col xs={12} md={6}>
                          <ListUnstyled>
                            {firstColApplicant.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ListUnstyled>
                        </Col>
                        <Col xs={12} md={6}>
                          <ListUnstyled>
                            {secondColApplicant.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ListUnstyled>
                        </Col>
                      </Row>
                    </DivNeutral50>
                  </BoxGrid>
                </GridItem>
              </GridLayout>
            </Col>
          </RowMargined>

          <Row center="xs">
            <Col xs={12}>
              <H3Neutral50Margined>{t('coreHRFeatures')}</H3Neutral50Margined>
            </Col>
          </Row>
          <GridLayout Col={12} autoRows>
            <GridItem Col={7}>
              <BoxGrid className={fullWidth} backgroundColor="transparent" border="green300">
                <DivNeutral50>
                  <H4DotNeonVioletFlowers>{t('hrInformationSystem')}</H4DotNeonVioletFlowers>
                  <Row className={styles['feature-lists']}>
                    <Col xs={12} md={6}>
                      <ListUnstyled>
                        {firstColHRInformation.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ListUnstyled>
                    </Col>
                    <Col xs={12} md={6}>
                      <ListUnstyled>
                        {secondColHRInformation.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ListUnstyled>
                    </Col>
                  </Row>
                </DivNeutral50>
              </BoxGrid>
            </GridItem>
            <GridItem Col={5}>
              <BoxGrid className={fullWidth} backgroundColor="transparent" border="green300">
                <DivNeutral50>
                  <H4DotNeonAegeanSea>{t('timeAndAttendanceSystem')}</H4DotNeonAegeanSea>
                  <ListUnstyled>
                    {TimeAttendance.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ListUnstyled>
                </DivNeutral50>
              </BoxGrid>
            </GridItem>
          </GridLayout>
        </Grid>
      </Container>
    </Green900Section>
  );
};

export default PlansFeatures;
